import {
  map,
  mergeDeepWith,
  reduce,
  isEmpty,
  concat,
  uniq,
  pipe,
  when,
  init,
} from 'ramda';
import { findAssociationsStatic } from '../Model/avenyModelTestUtils';
import { IClientAssociationModel } from '../Types/ClientModelTypes';
import { IColumnDef } from '../Types/ColumnDefType';
import { MASKED_FIELD_NAME } from '../Features/Delsystem/_Common/Hooks/useAttributeValues';

interface IFieldMap {
  _fields?: string[];
  _Associations?: { [key: string]: IFieldMap | null };
}

const reduceFieldMap =
  (associations: IClientAssociationModel[]) =>
  (acc: IFieldMap, name: string, index: number): IFieldMap => {
    if (isEmpty(acc)) {
      return { _fields: name === 'id' ? [name] : ['id', '_version', name] };
    } else {
      const isArray = !associations[index].JoinAssociation;
      const associationNameSuffix = isArray ? '_All' : '';

      return {
        _fields: ['id', '_version'],
        _Associations: {
          id: {},
          [`${name}${associationNameSuffix}`]: acc,
        },
      };
    }
  };

export const columnDefToFieldMap =
  (collectionName: string) =>
  (columnDef: string): IFieldMap => {
    const columnPath = columnDef.split('.');
    const associations = findAssociationsStatic(
      collectionName,
      init(columnPath)
    );
    const fieldMap = columnPath.reduceRight(reduceFieldMap(associations), {});
    return fieldMap;
  };

export const fieldMapToFields: (
  detaljurvalAssociationName?: string
) => (x: IFieldMap) => string = detaljurvalAssociationName => {
  const mapToFields = (x: IFieldMap): string => {
    return Object.entries(x)
      .map(([key, val]) => {
        if (key === '_fields') {
          return (val as string[]).join(' ');
        }

        const associationName = key.split('_')[0] ?? key;
        const paramString =
          associationName === detaljurvalAssociationName
            ? '(selection: $detaljurval)'
            : '';

        if (key === 'id') {
          return 'id';
        }

        return `${key}${paramString}{${mapToFields(val as IFieldMap)}}`;
      })
      .join(' ');
  };

  return mapToFields;
};

export const fieldDefsToFields: (
  collectionName: string,
  detaljurvalAssociationName?: string
) => (x: ReadonlyArray<string>) => string = (
  collectionName,
  detaljurvalAssociationName
) =>
  pipe(
    when(isEmpty, _ => ['id']),
    map(columnDefToFieldMap(collectionName)),
    reduce(
      mergeDeepWith((v1: ReadonlyArray<{}>, v2: ReadonlyArray<{}>) =>
        uniq(concat(v1, v2))
      ),
      { _fields: [MASKED_FIELD_NAME] } as IFieldMap
    ),
    fieldMapToFields(detaljurvalAssociationName)
  );

export const columnDefsToFields: (
  collectionName: string
) => (coldef: ReadonlyArray<IColumnDef>) => string = collectionName =>
  pipe(
    map((coldef: IColumnDef) => coldef.attributeDef || ''),
    fieldDefsToFields(collectionName)
  );
